import React, { PropsWithChildren, useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import {
  BodyText,
  Button,
  Container,
  useResponsive,
  DisplayText,
  FallbackImage,
  useOpenURL,
} from '@sg/garnish';

const PostOrderBannerFallbackImage = require('./post-order-banner-fallback.png');

// ─────────────────────────────────────────────────────────────────────────────

export const HomeOrderBanner = (props: HomeOrderBannerProps) => {
  const { title, description, image, imageAlt, deepLink, foregroundColor, cta, onPress } = props;
  const { currentBreakpoint, match } = useResponsive();

  // ───────────────────────────────────────────────────────────────────────────

  const openUrl = useOpenURL();
  const textColor = useStyle(() => ({ color: foregroundColor }), [foregroundColor]);

  const handleOnPress = useCallback(() => {
    openUrl(deepLink);
    onPress();
  }, [openUrl, deepLink, onPress]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HomeOrderBannerWrapper cta={cta} onPress={handleOnPress}>
      {currentBreakpoint.isXS ? (
        <FallbackImage
          baseUrl={image}
          alt={imageAlt}
          style={styles.image}
          contentfulOptions={IMAGE_CONFIG_XS}
        />
      ) : null}

      <View style={styles.content}>
        <View style={match([styles.infoXS, styles.infoSM])}>
          {currentBreakpoint.isXS ? (
            <BodyText style={textColor} sizeMatch={['24']} bold>
              {title}
            </BodyText>
          ) : (
            <DisplayText style={textColor} sizeMatch={['56']} bold>
              {title}
            </DisplayText>
          )}

          <BodyText style={textColor} sizeMatch={['16', '24']}>
            {description}
          </BodyText>
        </View>
      </View>

      {currentBreakpoint.isXS ? null : (
        <Button
          size={match(['medium', 'large'])}
          palette="whiteFill"
          accessibilityLabel={cta}
          onPress={handleOnPress}
        >
          {cta}
        </Button>
      )}
    </HomeOrderBannerWrapper>
  );
};

const HomeOrderBannerWrapper = (props: HomeOrderBannerWrapperProps) => {
  const { children, cta, onPress } = props;
  const { match, currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <Container style={match([styles.containerXS, styles.containerSM])}>
        <Pressable
          style={match([styles.cardXS, styles.cardSM])}
          accessibilityRole={'link' as const}
          aria-label={cta}
          onPress={onPress}
        >
          {children}
        </Pressable>
      </Container>
    );
  }

  return (
    <View style={match([styles.containerXS, styles.containerSM])}>
      <View style={match([styles.cardXS, styles.cardSM])}>{children}</View>
    </View>
  );
};

const HomeOrderBannerImage = (props: HomeOrderBannerImageProps) => {
  const { image, imageAlt } = props;
  const { match } = useResponsive();

  return (
    <FallbackImage
      defaultImage={PostOrderBannerFallbackImage}
      baseUrl={image}
      alt={imageAlt}
      style={styles.image}
      contentfulOptions={match([
        IMAGE_CONFIG_XS,
        IMAGE_CONFIG_SM,
        IMAGE_CONFIG_LG,
        IMAGE_CONFIG_XL,
      ])}
    />
  );
};

HomeOrderBanner.Image = HomeOrderBannerImage;

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_SIZE_XS = 300;
const IMAGE_SIZE_SM = 720;
const IMAGE_SIZE_LG = 1440;
const IMAGE_SIZE_XL = 1920;

const SHARED_IMAGE_CONFIG = { fm: 'webp' as const, fit: 'fill' as const, q: 70 };
const IMAGE_CONFIG_XS = { ...SHARED_IMAGE_CONFIG, h: IMAGE_SIZE_XS };
const IMAGE_CONFIG_SM = { ...SHARED_IMAGE_CONFIG, w: IMAGE_SIZE_SM };
const IMAGE_CONFIG_LG = { ...SHARED_IMAGE_CONFIG, w: IMAGE_SIZE_LG };
const IMAGE_CONFIG_XL = { ...SHARED_IMAGE_CONFIG, w: IMAGE_SIZE_XL };

const styles = StyleSheet.create({
  containerXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  containerSM: {
    flex: 1,
  },
  cardXS: {
    overflow: 'hidden',
    height: IMAGE_SIZE_XS,
    padding: theme.spacing['6'],
    gap: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
  },
  cardSM: {
    gap: theme.spacing['6'],
  },
  content: {
    marginTop: 'auto',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoXS: {
    flex: 1,
    gap: theme.spacing['2'],
  },
  infoSM: {
    flex: 1,
    gap: theme.spacing['6'],
  },
  image: {
    ...StyleSheet.absoluteFill,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeOrderBannerProps = {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  deepLink: string;
  foregroundColor: string;
  cta: string;
  onPress: () => void;
};

type HomeOrderBannerWrapperProps = PropsWithChildren & {
  cta: string;
  onPress: () => void;
};

type HomeOrderBannerImageProps = {
  image: string;
  imageAlt: string;
};
